import { isStatusCodeError } from '@confluence/error-types/entry-points/isStatusCodeError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useExperimentalQuery } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalQuery.ts';
import type { QueryResult } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalQuery.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { useStudioSpaceAri } from './useStudioSpaceAri.ts';

/** A page status referenced by a page-status-changed trigger (only the field we read). */
interface StudioPageStatus {
	label?: string;
}

export interface StudioRuleComponent {
	/** High-level kind, e.g. `TRIGGER`, `ACTION`, `CONDITION`. */
	component?: string;
	/** The specific type id, e.g. `rovo.invoke-agent.action` or `action.send-templated-email`. */
	type?: string;
	value?: {
		/** Present on `confluence.page-status-changed.trigger` triggers. */
		pageStatuses?: StudioPageStatus[];
	};
}
export interface StudioRuleEntry {
	name?: string;
	description?: string;
	trigger?: StudioRuleComponent;
	components?: StudioRuleComponent[];
}

const RULES_FETCH_LIMIT = 50;

type StudioRulesResponse = {
	values?: StudioRuleEntry[];
};

type StudioRulesData = {
	rules: StudioRuleEntry[];
};

type StudioRulesVariables = {
	authorAccountId: string;
	limit: number;
};

type UseExistingStudioRulesProps = {
	/** Skips the fetch entirely. Use this to defer the network call until a nudge would otherwise show. */
	skip?: boolean;
};

type UseExistingStudioRulesResponse = {
	rules: StudioRuleEntry[];
	isLoading: boolean;
	error?: QueryResult<StudioRulesData>['error'];
};

/**
 * Fetches the automation rules for the site's linked Studio space via the SC3 rules list endpoint.
 *
 * The workspace/space are resolved from {@link useStudioSpaceAri}. The request body is built entirely
 * inside this hook (constant `limit`, `authorAccountId` from session data) so that its
 * `useExperimentalQuery` cache key is byte-identical for every caller. Combined with `cache-first`
 * and the shared sweet-state cache, this means the network request fires at most once per
 * (workspace, space, user) per session, no matter how many nudges consume it.
 */
export const useExistingStudioRules = ({
	skip = false,
}: UseExistingStudioRulesProps = {}): UseExistingStudioRulesResponse => {
	const { studioSpaceAri, isLoading: isStudioSpaceLoading } = useStudioSpaceAri({ skip });
	const { userId, environment } = useSessionData();

	const workspaceId = studioSpaceAri?.workspaceId;
	const spaceId = studioSpaceAri?.spaceId;
	const envSegment = environment === 'STAGING' ? 'staging' : 'pro';
	const shouldRunQuery = !skip && Boolean(workspaceId) && Boolean(spaceId);

	const { data, loading, error } = useExperimentalQuery<
		StudioRulesData,
		StudioRulesVariables,
		StudioRulesResponse
	>({
		url: `/gateway/api/automation/sc3/o/studio/t/workspace/i/${workspaceId}/internal-api/${envSegment}/rest/${spaceId}/rules`,
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		credentials: 'include',
		variables: {
			authorAccountId: userId ?? '',
			limit: RULES_FETCH_LIMIT,
		},
		parseResult: (response) => ({
			rules: Array.isArray(response?.values) ? response.values : [],
		}),
		skip: !shouldRunQuery,
		fetchPolicy: 'cache-first',
		onError: (error) => {
			if (isStatusCodeError(error, '403') || isStatusCodeError(error, '404')) {
				markErrorAsHandled(error);
			}
		},
	});

	return {
		rules: data?.rules ?? [],
		isLoading: (shouldRunQuery && loading) || (!skip && isStudioSpaceLoading),
		error,
	};
};
